/**
 * Button — Triggers actions or events. The most common interactive element.
 *
 * Button Component Guidelines
 * - Choose `variant` based on visual hierarchy: `primary` for the main action on
 *   a page/modal, `secondary` for supporting actions, `tertiary` for low-emphasis
 *   actions (toolbars, inline), `destructive` for irreversible actions, and `link`
 *   for inline text navigation.
 * - Use `size="sm"` (24px) in dense areas like toolbars, table rows, or popovers;
 *   `size="base"` (32px) for standard forms and dialogs; `size="lg"` (40px) for
 *   hero CTAs or standalone page actions.
 * - Set `icon` when the button contains only an icon — it makes the button square.
 *   Pair icon-only buttons with a Tooltip so the action is still discoverable.
 * - Use `loading` during async operations — it disables the button and swaps the
 *   label for a spinner, so the same button can represent both idle and pending states.
 * - Use `startEnhancer` / `endEnhancer` for icons next to text. Don't place icons
 *   directly in `children` alongside text — the enhancer slots handle spacing and
 *   sizing automatically.
 * @see https://base-ui.com/react/components/button Base UI Button documentation
 * @see https://ui.shadcn.com/docs/components/button shadcn/ui Button documentation
 */
import type { VariantProps } from "class-variance-authority";
import type { DeprecatedLayoutClassName } from "../../types/deprecatedLayoutClassName";

import React from "react";

import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva } from "class-variance-authority";
import { LoaderCircle } from "lucide-react";

import twMerge from "../../utils/twMerge";

const buttonVariants = cva(
  "relative w-fit focus-visible:ring-ring/50 focus-visible:ring-3 aria-invalid:ring-rose-border aria-invalid:border-rose-border rounded-lg border border-0.5 border-transparent bg-clip-padding aria-invalid:ring-[3px] inline-flex items-center justify-center whitespace-nowrap transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none group/button select-none gap-2 shadow-2xs transition-opacity transition-colors duration-150",
  {
    variants: {
      variant: {
        primary:
          "bg-button-primary text-on-button-primary hover:bg-button-primary-hover focus-visible:border-ring",
        secondary:
          "border-border bg-button-secondary text-on-button-secondary hover:bg-button-secondary-hover hover:text-on-button-secondary-hover aria-expanded:bg-button-secondary-hover aria-expanded:text-on-button-secondary-hover",
        destructive:
          "border-rose-border bg-rose-soft hover:text-white hover:bg-button-danger-hover text-rose shadow-2xs focus-visible:ring-rose-border",
        tertiary:
          "text-default hover:bg-button-tertiary-hover aria-expanded:bg-button-tertiary-hover shadow-none",
        link: "text-default underline-offset-4 hover:underline shadow-none px-0! h-fit! font-medium",
      },
      size: {
        sm: "text-xs [&_svg:not([class*='size-'])]:size-3 rounded-md",
        base: "text-sm [&_svg:not([class*='size-'])]:size-3.5",
        lg: "text-base [&_svg:not([class*='size-'])]:size-4",
      },
      icon: {
        true: "",
        false: "",
      },
      align: {
        center: "justify-center",
        start: "justify-start",
      },
      allowShrink: {
        true: "min-w-0 shrink",
        false: "shrink-0",
      },
    },
    compoundVariants: [
      { icon: false, size: "sm", className: "h-6 px-2" },
      { icon: false, size: "base", className: "h-8 px-2.5" },
      { icon: false, size: "lg", className: "h-10 px-3" },
      { icon: true, size: "sm", className: "size-6 p-2" },
      { icon: true, size: "base", className: "size-8 p-2.5" },
      { icon: true, size: "lg", className: "size-10 p-3" },
    ],
    defaultVariants: {
      variant: "primary",
      size: "base",
      icon: false,
      align: "center",
      allowShrink: false,
    },
  },
);

interface ButtonProps<ClassNameType extends string = string>
  extends Omit<ButtonPrimitive.Props, "className">,
    VariantProps<typeof buttonVariants> {
  /**
   * The native button type attribute. Omit collapses the Base UI discriminated
   * union (ButtonNativeProps | ButtonNonNativeProps), dropping this prop — so we
   * re-add it explicitly.
   */
  type?: "submit" | "reset" | "button";
  loading?: boolean;
  startEnhancer?: React.ReactNode;
  endEnhancer?: React.ReactNode;
  truncate?: boolean;
  align?: "center" | "start";
  allowShrink?: boolean;
  /**
   * className with type safety for layout classes only
   */
  className?: DeprecatedLayoutClassName<ClassNameType>;
  /**
   * flexible className marked as UNSAFE to indicate it is circumventing the design system
   */
  UNSAFE_className?: string;
}

const ButtonInner = <ClassNameType extends string>(
  {
    variant = "primary",
    size = "base",
    icon = false,
    loading,
    startEnhancer,
    endEnhancer,
    truncate = false,
    align = "center",
    allowShrink = false,
    className,
    UNSAFE_className,
    ...props
  }: ButtonProps<ClassNameType>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) => {
  return (
    <ButtonPrimitive
      ref={ref}
      data-slot="button"
      disabled={props.disabled || loading}
      className={twMerge(
        buttonVariants({
          variant,
          size,
          icon,
          align,
          allowShrink,
        }),
        className,
        UNSAFE_className,
      )}
      {...props}
    >
      {!icon && startEnhancer && (
        <div
          className={twMerge(
            "transition-opacity duration-150",
            loading && "opacity-0",
          )}
        >
          {startEnhancer}
        </div>
      )}

      {props.children && (
        <span
          className={twMerge(
            "transition-opacity duration-150",
            truncate && "min-w-0 overflow-hidden",
            loading && "opacity-0",
          )}
        >
          {props.children}
        </span>
      )}

      <LoaderCircle
        className={twMerge(
          "animate-spin absolute transition-opacity duration-150",
          loading ? "opacity-100" : "opacity-0",
        )}
      />

      {!icon && endEnhancer && (
        <span
          className={twMerge(
            "transition-opacity duration-150",
            loading && "opacity-0",
          )}
        >
          {endEnhancer}
        </span>
      )}
    </ButtonPrimitive>
  );
};

// NOTE (Ben, 01/13/2026): Type cast to preserve generic type parameter for className prop
// See: https://fettblog.eu/typescript-react-generic-forward-refs/
const Button = React.forwardRef(ButtonInner) as <ClassNameType extends string>(
  props: ButtonProps<ClassNameType> & React.RefAttributes<HTMLButtonElement>,
) => React.ReactNode;

export { Button };
